/**
 * Hook to fetch and manage GMB Opening Hours data
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { getEffectiveHoursForDate, getUpcomingSpecialHours, isStoreOpenAt, isStoreClosedAt, getGmbStoreStatus, gmbHoursToStoreHours, checkSlotAvailabilityGMB } from './gmbOpeningHoursUtils';
const GMB_API_URL = 'https://api.miscusi.com/api/GCP/OpeningHours?fast=true';

// Cache for GMB data to avoid refetching
let gmbDataCache = null;
let gmbDataCacheTime = null;
const CACHE_DURATION_MS = 60 * 60 * 1000; // 1 hour cache

/**
 * Fetch all GMB opening hours data
 * @returns {Promise<import('@Src_main/types/openingHours').OpeningHoursData|null>}
 */
async function fetchGmbData() {
  // Check cache
  if (gmbDataCache && gmbDataCacheTime && Date.now() - gmbDataCacheTime < CACHE_DURATION_MS) {
    return gmbDataCache;
  }
  try {
    const response = await fetch(GMB_API_URL);
    if (!response.ok) {
      console.error('Failed to fetch GMB opening hours:', response.statusText);
      return null;
    }
    const data = await response.json();

    // Update cache
    gmbDataCache = data;
    gmbDataCacheTime = Date.now();
    return data;
  } catch (error) {
    console.error('Error fetching GMB opening hours:', error);
    return null;
  }
}

/**
 * Clear the GMB data cache (useful for testing or forced refresh)
 */
export function clearGmbCache() {
  gmbDataCache = null;
  gmbDataCacheTime = null;
}

/**
 * Hook to fetch and use GMB opening hours for a specific location
 * @param {string|null} gmbLocationId - The GMB location ID (numeric string)
 * @returns {{
 *   regularHours: import('@Src_main/types/openingHours').RegularHours|null,
 *   specialHours: import('@Src_main/types/openingHours').SpecialHours|null,
 *   loading: boolean,
 *   error: Error|null,
 *   isOpenAt: (datetime: Date) => boolean,
 *   isClosedAt: (datetime: Date) => boolean,
 *   getHoursForDate: (date: Date) => import('@Src_main/types/openingHours').EffectiveHours|null,
 *   checkSlotAvailability: (timestamp: string) => { available: boolean, reason: string|null },
 *   upcomingSpecialHours: import('@Src_main/types/openingHours').SpecialHourPeriod[],
 *   storeStatus: { isOpen: boolean, todayHours: EffectiveHours, tomorrowHours: EffectiveHours }|null,
 *   storeHoursFormat: Object,
 *   hasGmbData: boolean
 * }}
 */
export function useGmbOpeningHours(gmbLocationId) {
  const [regularHours, setRegularHours] = useState(null);
  const [specialHours, setSpecialHours] = useState(null);
  const [loading, setLoading] = useState(!!gmbLocationId);
  const [error, setError] = useState(null);
  useEffect(() => {
    if (!gmbLocationId) {
      setRegularHours(null);
      setSpecialHours(null);
      setLoading(false);
      return;
    }
    let mounted = true;
    setLoading(true);
    setError(null);
    fetchGmbData().then(data => {
      if (!mounted) return;
      if (!(data !== null && data !== void 0 && data.locations)) {
        setRegularHours(null);
        setSpecialHours(null);
        setLoading(false);
        return;
      }

      // Find the location that matches the gmbLocationId
      // The name format is 'locations/{locationId}'
      const location = data.locations.find(loc => loc.name === "locations/".concat(gmbLocationId));
      if (location) {
        setRegularHours(location.regularHours || null);
        setSpecialHours(location.specialHours || null);
      } else {
        console.warn("[GMB Hook] GMB location not found for ID: ".concat(gmbLocationId));
        setRegularHours(null);
        setSpecialHours(null);
      }
      setLoading(false);
    }).catch(err => {
      if (!mounted) return;
      console.error('Error in useGmbOpeningHours:', err);
      setError(err);
      setLoading(false);
    });
    return () => {
      mounted = false;
    };
  }, [gmbLocationId]);

  // Memoized helper functions
  const isOpenAt = useCallback(datetime => {
    if (!regularHours) return true; // Fallback: assume open if no GMB data
    return isStoreOpenAt(datetime, regularHours, specialHours);
  }, [regularHours, specialHours]);
  const isClosedAt = useCallback(datetime => {
    if (!regularHours) return false; // Fallback: assume open if no GMB data
    return isStoreClosedAt(datetime, regularHours, specialHours);
  }, [regularHours, specialHours]);
  const getHoursForDate = useCallback(date => {
    if (!regularHours) return null;
    return getEffectiveHoursForDate(date, regularHours, specialHours);
  }, [regularHours, specialHours]);
  const checkSlotAvailability = useCallback(timestamp => {
    if (!regularHours) {
      return {
        available: true,
        reason: null
      }; // Fallback: assume available if no GMB data
    }
    return checkSlotAvailabilityGMB(timestamp, regularHours, specialHours);
  }, [regularHours, specialHours]);
  const upcomingSpecialHours = useMemo(() => {
    return getUpcomingSpecialHours(specialHours, 30);
  }, [specialHours]);
  const storeStatus = useMemo(() => {
    if (!regularHours) return null;
    return getGmbStoreStatus(regularHours, specialHours);
  }, [regularHours, specialHours]);

  // Convert GMB hours to the store_hours format used elsewhere in the app
  const storeHoursFormat = useMemo(() => {
    if (!regularHours) return {};
    return gmbHoursToStoreHours(regularHours);
  }, [regularHours]);
  const hasGmbData = !!regularHours;
  return {
    regularHours,
    specialHours,
    loading,
    error,
    isOpenAt,
    isClosedAt,
    getHoursForDate,
    checkSlotAvailability,
    upcomingSpecialHours,
    storeStatus,
    storeHoursFormat,
    hasGmbData
  };
}

/**
 * Hook to fetch GMB opening hours for multiple stores at once
 * Useful for store lists where you want to show status for all stores
 * @param {Array<{ls_store_id: string, gmbLocationId: string}>} storesWithGmbIds
 * @returns {{
 *   hoursData: Map<string, { regularHours, specialHours }>,
 *   loading: boolean,
 *   error: Error|null
 * }}
 */
export function useGmbOpeningHoursMultiple(storesWithGmbIds) {
  const [hoursData, setHoursData] = useState(new Map());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  useEffect(() => {
    if (!storesWithGmbIds || storesWithGmbIds.length === 0) {
      setHoursData(new Map());
      setLoading(false);
      return;
    }
    let mounted = true;
    setLoading(true);
    fetchGmbData().then(data => {
      if (!mounted) return;
      const newHoursData = new Map();
      if (data !== null && data !== void 0 && data.locations) {
        storesWithGmbIds.forEach(store => {
          if (!store.gmbLocationId) return;
          const location = data.locations.find(loc => loc.name === "locations/".concat(store.gmbLocationId));
          if (location) {
            newHoursData.set(store.ls_store_id, {
              regularHours: location.regularHours || null,
              specialHours: location.specialHours || null
            });
          }
        });
      }
      setHoursData(newHoursData);
      setLoading(false);
    }).catch(err => {
      if (!mounted) return;
      setError(err);
      setLoading(false);
    });
    return () => {
      mounted = false;
    };
  }, [storesWithGmbIds]);
  return {
    hoursData,
    loading,
    error
  };
}
export default useGmbOpeningHours;